import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { getProductionStats } from "../../services/supervisorService";
import {
    AiOutlineBarChart,
    AiOutlineClockCircle,
    AiOutlineCheck,
    AiOutlineWarning,
    AiOutlineReload
} from "react-icons/ai";
import { format, subDays } from 'date-fns';

function ProductionStats({ showPopup }) {
    const [stats, setStats] = useState({
        summary: {
            total_production: 0,
            total_workdays: 0,
            total_activities: 0,
            total_issues: 0
        },
        production_by_product: [],
        daily_production: []
    });
    const [isLoading, setIsLoading] = useState(true);
    const [dateRange, setDateRange] = useState({
        start_date: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
        end_date: format(new Date(), 'yyyy-MM-dd')
    });

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        setIsLoading(true);
        try {
            const response = await getProductionStats(dateRange);
            if (response.success && response.data) {
                setStats(response.data);
            } else {
                showPopup("Failed to load production statistics", "error");
            }
        } catch (error) {
            console.error("Error fetching production stats:", error);
            showPopup("Failed to load production statistics", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setDateRange({
            ...dateRange,
            [name]: value
        });
    };

    // Helper function to get colors for bars
    const getColorForIndex = (index) => {
        const colors = ['#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#F44336', '#607D8B'];
        return colors[index % colors.length];
    };

    return (
        <Container>
            <Header>
                <h2>Production Statistics</h2>
                <RefreshButton onClick={fetchStats}>
                    <AiOutlineReload />
                </RefreshButton>
            </Header>

            <DateFilterRow>
                <DateLabel>
                    Start Date:
                    <DateInput
                        type="date"
                        name="start_date"
                        value={dateRange.start_date}
                        onChange={handleDateChange}
                    />
                </DateLabel>

                <DateLabel>
                    End Date:
                    <DateInput
                        type="date"
                        name="end_date"
                        value={dateRange.end_date}
                        onChange={handleDateChange}
                    />
                </DateLabel>
            </DateFilterRow>

            {isLoading ? (
                <LoadingState>Loading production statistics...</LoadingState>
            ) : (
                <ContentArea>
                    <StatsGrid>
                        <StatCard>
                            <StatIconContainer className="production">
                                <AiOutlineBarChart />
                            </StatIconContainer>
                            <StatContent>
                                <StatNumber>{stats.summary.total_production || 0}</StatNumber>
                                <StatText>Total Production</StatText>
                            </StatContent>
                        </StatCard>

                        <StatCard>
                            <StatIconContainer className="activity">
                                <AiOutlineClockCircle />
                            </StatIconContainer>
                            <StatContent>
                                <StatNumber>{stats.summary.total_activities || 0}</StatNumber>
                                <StatText>Total Activities</StatText>
                            </StatContent>
                        </StatCard>

                        <StatCard>
                            <StatIconContainer className="workdays">
                                <AiOutlineCheck />
                            </StatIconContainer>
                            <StatContent>
                                <StatNumber>{stats.summary.total_workdays || 0}</StatNumber>
                                <StatText>Work Days</StatText>
                            </StatContent>
                        </StatCard>

                        <StatCard>
                            <StatIconContainer className="issues">
                                <AiOutlineWarning />
                            </StatIconContainer>
                            <StatContent>
                                <StatNumber>{stats.summary.total_issues || 0}</StatNumber>
                                <StatText>Issues Reported</StatText>
                            </StatContent>
                        </StatCard>
                    </StatsGrid>

                    <ChartsContainer>
                        <ChartBox>
                            <ChartTitle>Production by Product</ChartTitle>
                            {stats.production_by_product && stats.production_by_product.length > 0 ? (
                                <ChartContent>
                                    {stats.production_by_product.map((item, index) => (
                                        <ProductBarRow key={index}>
                                            <ProductName>{item.name}</ProductName>
                                            <BarWrapper>
                                                <ProductBar
                                                    width={Math.min((item.total_quantity / Math.max(...stats.production_by_product.map(p => p.total_quantity || 0))) * 100, 100)}
                                                    color={getColorForIndex(index)}
                                                />
                                                <ProductValue>{item.total_quantity}</ProductValue>
                                            </BarWrapper>
                                        </ProductBarRow>
                                    ))}
                                </ChartContent>
                            ) : (
                                <EmptyState>No production data available</EmptyState>
                            )}
                        </ChartBox>

                        <ChartBox>
                            <ChartTitle>Daily Production Trend</ChartTitle>
                            {stats.daily_production && stats.daily_production.length > 0 ? (
                                <TrendChartContainer>
                                    <svg viewBox="0 0 400 200" preserveAspectRatio="none" width="100%" height="100%">
                                        {/* Horizontal middle axis */}
                                        <line x1="0" y1="150" x2="400" y2="150" stroke="#eee" strokeWidth="1" />

                                        {/* Trend line */}
                                        <polyline
                                            points={stats.daily_production.map((day, i) => {
                                                const x = (i / (stats.daily_production.length - 1)) * 400;
                                                const max = Math.max(...stats.daily_production.map(d => d.total_production || 0)) || 1;
                                                const y = 150 - (((day.total_production || 0) / max) * 100);
                                                return `${x},${y}`;
                                            }).join(' ')}
                                            fill="none"
                                            stroke="#2196F3"
                                            strokeWidth="2"
                                        />

                                        {/* Data points */}
                                        {stats.daily_production.map((day, i) => {
                                            const x = (i / (stats.daily_production.length - 1)) * 400;
                                            const max = Math.max(...stats.daily_production.map(d => d.total_production || 0)) || 1;
                                            const y = 150 - (((day.total_production || 0) / max) * 100);
                                            return (
                                                <circle key={i} cx={x} cy={y} r="4" fill="#2196F3" />
                                            );
                                        })}
                                    </svg>
                                </TrendChartContainer>
                            ) : (
                                <EmptyState>No daily production data available</EmptyState>
                            )}
                        </ChartBox>
                    </ChartsContainer>
                </ContentArea>
            )}
        </Container>
    );
}

export default ProductionStats;

// Styled Components
const Container = styled.div`
    background-color: white;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    padding: 20px;
    width: 100%;
    height: calc(100vh - 120px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h2 {
        font-size: 20px;
        font-weight: 500;
        margin: 0;
        color: #333;
    }
`;

const RefreshButton = styled.button`
    background: none;
    border: none;
    color: #777;
    cursor: pointer;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        color: #000;
    }
`;

const DateFilterRow = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 20px;
`;

const DateLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #555;
`;

const DateInput = styled.input`
    padding: 8px 10px;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 14px;

    &:focus {
        outline: none;
        border-color: #2196F3;
    }
`;

const LoadingState = styled.div`
    text-align: center;
    padding: 40px;
    color: #777;
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ContentArea = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow: hidden;
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 15px;
    margin-bottom: 20px;
`;

const StatCard = styled.div`
    display: flex;
    align-items: center;
    padding: 15px;
    background-color: #f9f9f9;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const StatIconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    margin-right: 15px;
    font-size: 22px;

    &.production {
        background-color: #e3f2fd;
        color: #2196F3;
    }

    &.activity {
        background-color: #e8f5e9;
        color: #4caf50;
    }

    &.workdays {
        background-color: #fff3e0;
        color: #ff9800;
    }

    &.issues {
        background-color: #ffebee;
        color: #f44336;
    }
`;

const StatContent = styled.div`
    display: flex;
    flex-direction: column;
`;

const StatNumber = styled.div`
    font-size: 24px;
    font-weight: 600;
    color: #333;
`;

const StatText = styled.div`
    font-size: 14px;
    color: #777;
    margin-top: 5px;
`;

const ChartsContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    flex-grow: 1;
    overflow: hidden;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const ChartBox = styled.div`
    background-color: #f9f9f9;
    border-radius: 10px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const ChartTitle = styled.h3`
    font-size: 16px;
    font-weight: 500;
    color: #444;
    margin: 0 0 15px 0;
`;

const ChartContent = styled.div`
    flex-grow: 1;
    overflow-y: auto;
    padding-right: 5px;
`;

const ProductBarRow = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 15px;
`;

const ProductName = styled.div`
    width: 110px;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #444;
`;

const BarWrapper = styled.div`
    flex-grow: 1;
    display: flex;
    align-items: center;
    height: 22px;
`;

const ProductBar = styled.div`
    height: 14px;
    width: ${props => props.width}%;
    background-color: ${props => props.color || '#2196F3'};
    border-radius: 7px;
    min-width: 10px;
`;

const ProductValue = styled.div`
    margin-left: 10px;
    font-size: 14px;
    font-weight: 500;
    color: #666;
`;

const TrendChartContainer = styled.div`
    flex-grow: 1;
    height: 100%;
    width: 100%;
    min-height: 200px;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 40px 15px;
    color: #999;
    font-style: italic;
    font-size: 14px;
    background-color: white;
    border-radius: 8px;
    margin-top: 20px;
`;